import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Invoice, PaymentStatus } from "@/api/opb/opbTypes";
import { Checkbox, TextButton } from "@industriousoffice/react-components";
import { formatCurrency } from "@/utils/string";
import { getFullPath } from "@/router/utils/getFullPath";
import { slugs } from "@/router/slugs";
import PaymentStatusTag from "@/components/PaymentStatusTag";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface InvoiceCardProps {
  invoice: Invoice;
  isInvoiceSelected?: boolean;
  onToggleInvoice?: (invoice: Invoice) => void;
  name: string;
  currencyCode: string;
  withCheckbox?: boolean;
  isProcessing?: boolean;
  accountId: string;
  isLast?: boolean;
  timezone?: string;
}
export function InvoiceCard({
  invoice,
  isInvoiceSelected,
  onToggleInvoice,
  name,
  currencyCode,
  withCheckbox,
  isProcessing,
  accountId,
  isLast,
  timezone,
}: InvoiceCardProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation("payInvoices");
  const navigate = useNavigate();
  const { format, formatDateFromYYYYMMDD } = useDateFormatter();

  const onChange = () => {
    onToggleInvoice?.(invoice);
  };
  const onNavigateToViewInvoicePage = () => {
    navigate(getFullPath(slugs.invoice, { invoiceId: invoice.id, accountId }));
  };
  return (
    <div
      className={`flex py-4 border rounded-lg ${
        isInvoiceSelected ? "border-teal-40 border-2!" : "border-grey-80"
      } sm:border-grey-60 ${
        isLast ? "sm:border-b-0!" : "sm:border-b!"
      } sm:border-0! sm:rounded-none`}
      data-testid={`pay_invoices_invoice_card-${invoice.id}`}
    >
      {withCheckbox ? (
        <div className="flex items-center mx-4 sm:mx-6">
          <Checkbox
            onChange={onChange}
            checked={isInvoiceSelected ?? false}
            data-testid="pay_invoices_invoice_card-checkbox"
            className="sm:mb-2"
          />
        </div>
      ) : null}
      <div className={`flex-1 ${!withCheckbox ? "ml-4 sm:ml-6" : ""}`}>
        <div className="flex flex-col-reverse text-sm sm:flex sm:flex-row">
          <div className="text-grey-30 font-normal sm:mr-8 sm:py-0 py-2">
            {invoice.invoiceNumber}
          </div>
          <div className="text-grey-30">
            {t("Issued", {
              date: format(invoice.createdAt, "PPP", timezone),
            })}
          </div>
        </div>
        <div className="hidden my-2 sm:block">{name}</div>
        <div className="flex">
          <PaymentStatusTag
            invoice={
              isProcessing
                ? { ...invoice, paymentStatus: PaymentStatus.Processing }
                : invoice
            }
            className="mr-2 text-xs sm:mr-4 sm:text-sm"
          />
          <span className="text-xs sm:text-sm">
            {formatDateFromYYYYMMDD(invoice.dueDate, "PPP")}
          </span>
        </div>
      </div>
      <div className="flex flex-col justify-end mx-3 sm:mx-4 align-end">
        <div className="mb-1 text-sm font-bold sm:text-xl">
          {formatCurrency({
            value:
              invoice.paymentStatus !== PaymentStatus.Paid
                ? invoice.balance
                : invoice.amount,
            currency: currencyCode,
            lang: language,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
        <TextButton
          type="button"
          onClick={onNavigateToViewInvoicePage}
          className="text-sm sm:text-md"
          data-testid="pay_invoices_invoice_card-view_invoice-button"
        >
          {t("View Invoice")}
        </TextButton>
      </div>
    </div>
  );
}
